import { useMutation } from '@tanstack/react-query';
import NextLink from 'next/link';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Input from 'shopper/components/Input';
import Text from 'shopper/components/Text';

import Image from 'components/Image';

import useToast from 'hooks/useToast';

import { toLandingPageLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { isEmailValid } from 'lib/string';

import { useUser } from 'providers/UserProvider';

import { postPreSignUpUser } from 'services/user';

import LANDINGS from 'constants/landings';
import LOGIN from 'constants/login';

const Promonews = (props) => {
  const { showToast } = useToast();
  const { isLoggedIn, loadingLoggedInUser } = useUser();
  const [isUserPreSigned, setUserPreSigned] = useState(false);
  const {
    control,
    reset: resetForm,
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: { name: '', email: '' },
    reValidateMode: 'onSubmit',
  });

  const { mutate: mutatePreSignUp, reset } = useMutation({
    mutationFn: postPreSignUpUser,
    onSuccess: () => {
      setUserPreSigned(true);
      reset();
      resetForm({ name: '', email: '' });
      showToast({
        iconLeft: <Icon name="info-sign" />,
        text: placeholder('toasts.successes.promonews.alertCreated'),
      });
    },
    onError: ({ message }) => {
      switch (message) {
        case 'email is required':
          setError('email', { type: 'request', message: LOGIN.EMAIL_REQUIRED });
          break;
        case LOGIN.ERRORS.EMAIL_INVALID:
          setError('email', {
            type: 'request',
            message: LOGIN.EMAIL_NOT_VALID,
          });
          break;
        case LOGIN.ERRORS.EMAIL_EXISTS:
          setError('email', {
            type: 'request',
            message: LOGIN.EMAIL_ALREADY_REGISTERED,
          });
          break;
        case LOGIN.ERRORS.NAME_REQUIRED:
          setError('name', { type: 'request', message: LOGIN.NAME_REQUIRED });
          break;
        default:
          break;
      }
    },
  });

  const onSubmit = (signInData) => mutatePreSignUp(signInData);

  // There is a clickable link inside the form that is submitted
  // on `Enter`, so we override this behavior submitting the form instead
  const onKeyDownHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  if (
    loadingLoggedInUser ||
    (!loadingLoggedInUser && isLoggedIn) ||
    isUserPreSigned
  ) {
    return null;
  }

  return (
    <div
      className="mb-6 rounded-2 bg-primary-400 px-4 py-6 md:px-16 md:py-12"
      {...props}
    >
      <div className="mb-4 flex content-center">
        <Image
          alt="Ícone do Promobit"
          height={28}
          src="/dist/images/features/promonews/promobit-logo.svg"
          width={28}
          aria-hidden
        />
        <Heading className="ml-2 text-neutral-high-100" size="size3">
          {placeholder('titles.promonews')}
        </Heading>
      </div>
      <Text className="mb-5 text-neutral-high-100">
        {placeholder('subtitles.promonews')}
      </Text>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-2 flex flex-wrap items-center md:flex-nowrap">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <Input
                className="mb-4 w-full md:mb-0 md:mr-2"
                error={!!error}
                message={error?.message}
                placeholder={placeholder('inputs.placeholders.name')}
                type="text"
                full
                {...field}
              />
            )}
            rules={{
              required: LOGIN.NAME_REQUIRED,
              validate: (value) => !!value || LOGIN.NAME_REQUIRED,
            }}
            required
          />
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <Input
                className="mb-4 md:mb-0 md:mr-2"
                error={!!error}
                message={error?.message}
                name="email"
                placeholder={placeholder('inputs.placeholders.email')}
                type="email"
                full
                onKeyDown={onKeyDownHandler}
                {...field}
              />
            )}
            rules={{
              required: LOGIN.EMAIL_REQUIRED,
              validate: (value) => isEmailValid(value) || LOGIN.EMAIL_NOT_VALID,
            }}
            required
          />
          <Button htmlType="submit" size="size3" type="secondary" full>
            {placeholder('actions.subscribeToPromonews')}
          </Button>
        </div>
      </form>
      <Text
        className="text-neutral-high-100 dark:text-neutral-high-100"
        size="size3"
      >
        Ao me cadastrar, concordo com os
        <NextLink
          href={toLandingPageLink('termos', LANDINGS.TYPES.INSTITUTIONAL)}
          prefetch={false}
          passHref
        >
          <Text
            as="a"
            className="mx-1 text-neutral-high-100 dark:text-neutral-high-100"
            size="size3"
            bold
          >
            Termos de uso
          </Text>
        </NextLink>
        e
        <NextLink
          href={toLandingPageLink(
            'politica-de-privacidade',
            LANDINGS.TYPES.INSTITUTIONAL
          )}
          prefetch={false}
          passHref
        >
          <Text
            as="a"
            className="mx-1 text-neutral-high-100 dark:text-neutral-high-100"
            size="size3"
            bold
          >
            Política de privacidade do Promobit
          </Text>
        </NextLink>
      </Text>
    </div>
  );
};

export default Promonews;
